import { ChoiceList } from "@cmsgov/ds-medicare-gov";
import { AxiosResponse } from "axios";
import { useContext, useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router";
import Button from "../../common/components/button";
import CancelButton from "../../common/components/CancelButton";
import Div from "../../common/components/div";
import H1 from "../../common/components/h1";
import H3 from "../../common/components/h3";
import { SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import StackingButtons from "../../common/components/StackingButtons";
import { reviewTermsAndConditionsAndPrivacyPolicy, textStartInstructions } from "../../common/components/MfaShared";
import { pushWithParams } from "../../common/functions/pushWithParams";
import translate, { TranslationKey } from "../../common/functions/translate";
import { useMfaInlineError, usePageView } from "../../common/hooks/use.analytics";
import useLoginApi, { MfaOption, MfaSendCodeResponse } from "../../common/hooks/useLoginApi";
import { globalData } from "../../globalContext";
import { SubmitButton } from "../buttons/Buttons";
import { HelpFooter } from "../shared-components/HelpFooter";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import { hasErrorMessage } from "../../common/functions/getApiErrorMessage";

type ErrorState = 'notSelected';

export const LoginSendCode = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessageState, setErrorMessageState] = useState<ErrorState | null>(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [showOptedOutForChosenMethod, setShowOptedOutForChosenMethod] = useState(false);
    const [showDeactivatedForChosenMethod, setShowDeactivatedForChosenMethod] = useState(false);
    const [apiResponse, setApiResponse] = useState<AxiosResponse<MfaSendCodeResponse> | null>(null);

    const history = useHistory();
    const { mfaSend } = useLoginApi();
    usePageTitle(translate('confirm_it_is_you'));
    usePageView("mfa login send code");
    const choicesLabel = translate("select_preferred_method_code");
    const inlineError = useMfaInlineError(choicesLabel);

    const { mfaLoginData, setMfaLoginData } = useContext(globalData);
    const { username, password, options: mfaOptions } = mfaLoginData;
    const [selectedOptionId, setSelectedOptionId] = useState<string | null>(() => {
        if (mfaOptions?.length === 1) {
            return mfaOptions[0].id;
        }
        return null;
    });

    useEffect(() => {
        if (selectedOptionId) {
            setErrorMessageState(null);
            return;
        }
        setErrorMessageState('notSelected');
    }, [selectedOptionId]);

    // missing data from context
    if (!username || !password || !mfaOptions) {
        return <Redirect to="/login" />;
    }

    const selectedOption = mfaOptions.find(x => x.id === selectedOptionId);

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setSubmitAttempted(true);
        if (errorMessageState) {
            inlineError.validationError(translate("select_your_preferred_method"));
            return;
        }

        setIsLoading(true);

        // multi options - select opted out method
        if (!showOptedOutForChosenMethod && selectedOption?.opted_out && mfaOptions!.length > 1) {
            setIsLoading(false);
            setShowOptedOutForChosenMethod(true);
            return;
        }

        // multi options - select deactivated method
        if (!showDeactivatedForChosenMethod && selectedOption?.deactivated && mfaOptions!.length > 1) {
            setIsLoading(false);
            setShowDeactivatedForChosenMethod(true);
            return;
        }

        const res = await mfaSend(selectedOption?.id || '');
        setApiResponse(res);
        const status = res?.status;
        if (status === 204) {
            setMfaLoginData({
                username,
                password,
                type: selectedOption?.type,
                value: selectedOption?.mfa_channel,
                id: selectedOption?.id
            });
            pushWithParams(history, '/security-code/login-enter-code');
        } else {
            setIsLoading(false);
        }

    }

    function updateOptionSelected(optionId: string) {
        setSelectedOptionId(optionId);
    }

    function getChoicesErrorMessage() {
        if (!submitAttempted || selectedOptionId) {
            return;
        }
        return translate("select_your_preferred_method");
    }

    const contactMeAtKeys: Record<MfaOption['type'], TranslationKey> = {
        sms: 'text_me_at',
        voice: 'call_me_at',
        email: 'email_me_at'
    };
    const options = mfaOptions.map(x => ({
        label: <>
            {translate(contactMeAtKeys[x.type])} <span className="ds-u-text-transform--uppercase ds-u-font-weight--semibold">{x.mfa_channel}</span>
        </>,
        value: x.id
    }));

    const unselectMethod = () => {
        setShowOptedOutForChosenMethod(false);
        setShowDeactivatedForChosenMethod(false);
        setSelectedOptionId(null);
        setSubmitAttempted(false);
        setApiResponse(null);
    }

    const phoneDisclaimers = (heading: string) => <>
        <p className={'ds-u-margin-bottom--0'}>{translate('message_rates_apply')}</p>
        <p className={'ds-u-margin-top--1'}>{reviewTermsAndConditionsAndPrivacyPolicy(heading)}</p>
    </>

    const optedOutBody = (headingTitle: string, option?: MfaOption) => {
        const multiMethods = mfaOptions.length > 1;
        return <Div isRow>
            <Div cols={12} colsMD={8}>
                <p className="ds-u-margin-bottom--3 ds-u-margin-top--2">{translate('you_opted_out_of_sms')} <span className="ds-u-font-weight--bold ds-u-text-transform--uppercase">{option?.mfa_channel}</span>.</p>
                <p className='ds-u-margin-bottom--1'>{translate('to_start_getting_codes_again')}</p>
                { textStartInstructions }
                { phoneDisclaimers(headingTitle) }
                {multiMethods &&
                    <p className="ds-u-margin-y--4">{translate('you_can_also_select_back')}</p>
                }
                <StackingButtons
                    button1={
                        <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                            text: translate('send_my_code_sms'),
                            parentComponentHeading: headingTitle
                        }}>
                            {translate('send_my_code_sms')}
                        </SubmitButton>
                    }
                    button2={
                        multiMethods ?
                            <Button onClick={unselectMethod} mfaAnalyticsData={{
                                text: translate('back'),
                                parentComponentHeading: headingTitle
                            }}>
                                {translate('back')}
                            </Button> :
                            <CancelButton mfaAnalyticsHeading={headingTitle} onClick={() => history.goBack()}/>
                    }
                />
            </Div>
        </Div>
    };

    const deactivatedBody = (headingTitle: string, option?: MfaOption) => {
        const isSms = option?.type === 'sms';
        const phoneChanged: TranslationKey = isSms ? 'your_mobile_phone_changed' : 'your_phone_changed';
        const sendButtonText = isSms ? translate('send_my_code_sms') : translate('call_me_with_code');
        const multiMethods = mfaOptions.length > 1;

        let instructions = <p>
            {translate('if_you_still_want_codes')} <span className="ds-u-font-weight--bold  ds-u-text-transform--uppercase">{option?.mfa_channel}</span>,{' '}
            {translate('select_button_text_period', { buttonText: sendButtonText })}{' '}
            {translate('if_your_phone_number_changed')}</p>
        if (multiMethods) {
            instructions = <p>
                {translate('if_you_still_want_codes')} <span className="ds-u-font-weight--bold  ds-u-text-transform--uppercase">{option?.mfa_channel}</span>, {translate('select_button_text', { buttonText: sendButtonText })}{' '}
                {translate('or_select_back')}{' '}
                {translate('if_your_phone_number_changed')}
            </p>
        }

        return <Div isRow>
            <Div cols={12} colsMD={8}>
                <p className="ds-u-margin-bottom--3">{translate(phoneChanged)}</p>
                { instructions }
                { isSms && phoneDisclaimers(headingTitle) }
                <StackingButtons
                    button1={
                        <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                            text: sendButtonText,
                            parentComponentHeading: headingTitle
                        }}>
                            {sendButtonText}
                        </SubmitButton>
                    }
                    button2={
                        multiMethods ?
                            <Button onClick={unselectMethod} mfaAnalyticsData={{
                                text: translate('back'),
                                parentComponentHeading: headingTitle
                            }}>
                                {translate('back')}
                            </Button> :
                            <CancelButton mfaAnalyticsHeading={headingTitle} onClick={() => history.goBack()}/>
                    }
                />
            </Div>
        </Div>
    };

    const choicesH3Text = translate("select_preferred_method_code");
    const choicesBody = <>
        <ChoiceList
            label={
                <H3 className={"ds-u-margin-bottom--1"} style={{ textTransform: 'none' }}>
                    {choicesH3Text}
                </H3>
            }
            labelClassName={"ds-u-measure--wide"}
            className={'ds-u-margin-bottom--3 mfa-choice-list'}
            name={'selectSecurityMethod'}
            onChange={(e) => updateOptionSelected(e.target.value)}
            type={'radio'}
            choices={options}
            errorMessage={getChoicesErrorMessage()}
        />
        <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
            text: translate('send_my_code'),
            parentComponentHeading: choicesH3Text
        }}>
            {translate('send_my_code')}
        </SubmitButton>
    </>


    const hasOptedOutMethod = mfaOptions.some(x => x.opted_out);
    const showOptedOutInstructions = (mfaOptions.length === 1 && hasOptedOutMethod) || showOptedOutForChosenMethod;
    const hasDeactivatedMethod = mfaOptions.some(x => x.deactivated);
    const showDeactivatedInstructions = (mfaOptions.length === 1 && hasDeactivatedMethod) || showDeactivatedForChosenMethod;

    const headingTitle = showOptedOutInstructions || showDeactivatedInstructions ? translate('we_couldnt_send_code') : translate("confirm_it_is_you")
    let body = choicesBody;
    const hasOptedOutError = hasErrorMessage(apiResponse?.data.errors || [], 'MFA_USER_OPTED_OUT');
    const hasDeactivatedError = hasErrorMessage(apiResponse?.data.errors || [], 'MFA_DEACTIVATED_PHONE');
    const hasInvalidIdError = hasErrorMessage(apiResponse?.data.errors || [], 'MFA_METHOD_ID_INVALID');
    let hideAlert = hasOptedOutError || hasDeactivatedError || hasInvalidIdError;
    if (showOptedOutInstructions) {
        body = optedOutBody(headingTitle, selectedOption);
        hideAlert = false;
    }
    if (showDeactivatedInstructions) {
        body = deactivatedBody(headingTitle, selectedOption);
        hideAlert = false;
    }

    const helpFooterTitle: TranslationKey = showOptedOutInstructions ? 'cannot_access_your_sms' : 'new_phone_number';

    return (
        <Div isContainer paddingy={1} paddingyMD={4}>
            <form onSubmit={onSubmit} method="POST">
                <Div isRow>
                    <Div cols={12}>
                        <H1>
                            {headingTitle}
                        </H1>
                    </Div>
                </Div>
                {!hideAlert && <SmartErrorAlert
                    className={'ds-u-margin-top--3'}
                    name={"MfaLoginSendCode"}
                    fieldRef={null}
                    apiResponse={apiResponse}
                    conversion={null}
                    invalidFields={null}
                    loginFailCount={0}
                    onInvalidField={() => {}}
                    parentHeading={translate('confirm_it_is_you')}
                />}
                { body }
                <Div marginTop={7}>
                    {
                        showOptedOutInstructions || showDeactivatedInstructions
                            ? <HelpFooter title={translate(helpFooterTitle)}/>
                            : <HelpFooter title={translate("cannot_access_these_methods")}/>
                    }
                </Div>
            </form>
        </Div>
    );
}